import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, BookOpen, Home, Radio, Sparkles, LogIn, LogOut, Shield } from "lucide-react";
import { SEASON_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";

const QUOTES = [
  "UNBREAKABLE!",
  "Females are strong as hell.",
  "Pinot Noir!",
  "Peeno Noir, an ode to black penis.",
  "I'm gonna work at the H&R Block.",
  "That's our word!",
  "Hashbrown no filter.",
  "Damn, Daniel.",
  "I learned that on The Apprentice!",
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [q, setQ] = React.useState("");

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="App min-h-screen" data-testid="app-layout">
      {/* Top marquee */}
      <div
        className="bg-pink-500 text-white border-b-2 border-slate-900 overflow-hidden crt-scanlines"
        data-testid="top-marquee"
      >
        <div className="marquee-track py-2 font-mono-display text-sm uppercase tracking-widest">
          {[...Array(4)].map((_, i) => (
            <span key={i} className="flex gap-12 pr-12">
              {QUOTES.map((qt, j) => (
                <span key={j}>✱ {qt}</span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Chunky Win95-style title bar */}
      <header className="bg-slate-200 border-b-4 border-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3 no-underline" data-testid="home-link">
            <div className="w-12 h-12 bg-yellow-400 brutal-border brutal-shadow flex items-center justify-center font-mono-display font-bold text-2xl">
              K!
            </div>
            <div>
              <div className="font-mono-display text-xl md:text-2xl font-bold tracking-tighter uppercase text-slate-900">
                Kimmypedia
              </div>
              <div className="font-mono-display text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-600">
                The Unbreakable Kimmy Schmidt Lexicon
              </div>
            </div>
          </Link>

          <form onSubmit={onSearch} className="flex gap-2 items-stretch flex-1 max-w-xl min-w-[220px]">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the bunker..."
              className="brutal-input font-mono-display"
              data-testid="header-search-input"
            />
            <button type="submit" className="brutal-btn primary" data-testid="header-search-button">
              <Search size={16} strokeWidth={3} />
              Search
            </button>
          </form>
        </div>

        {/* sub-nav chunky */}
        <nav className="bg-slate-900 text-white border-t-2 border-slate-900">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-0 overflow-x-auto">
            <NavItem to="/" icon={<Home size={16} strokeWidth={2.5} />} label="Home" testid="nav-home" end />
            <NavItem to="/episodes" icon={<BookOpen size={16} strokeWidth={2.5} />} label="Episodes" testid="nav-episodes" />
            <NavItem to="/search" icon={<Search size={16} strokeWidth={2.5} />} label="Search" testid="nav-search" />
            <div className="ml-auto flex items-center gap-2 py-2 pr-2">
              {user ? (
                <>
                  <NavLink to="/admin/reports" className={({ isActive }) => `hidden md:flex items-center gap-1 brutal-btn !py-1 !px-2 text-xs ${isActive ? "primary" : ""}`} data-testid="nav-admin-reports">
                    <Shield size={12} strokeWidth={3} /> Reports
                  </NavLink>
                  <NavLink to="/admin/audit" className={({ isActive }) => `hidden md:flex items-center gap-1 brutal-btn !py-1 !px-2 text-xs ${isActive ? "primary" : ""}`} data-testid="nav-admin-audit">
                    <Shield size={12} strokeWidth={3} /> Audit
                  </NavLink>
                  <div className="hidden sm:flex items-center gap-2 bg-yellow-400 text-slate-900 brutal-border px-2 py-1 font-mono-display text-[10px] uppercase tracking-widest" data-testid="admin-badge">
                    <Shield size={12} strokeWidth={3} /> Admin
                  </div>
                  <button onClick={() => { logout(); navigate("/"); }} className="brutal-btn" data-testid="logout-button">
                    <LogOut size={14} strokeWidth={3} /> Logout
                  </button>
                </>
              ) : (
                <Link to="/login" className="brutal-btn secondary" data-testid="login-link">
                  <LogIn size={14} strokeWidth={3} /> Admin
                </Link>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="hidden lg:block">
          <Sidebar />
        </aside>
        <main>{children}</main>
      </div>

      {/* Footer */}
      <footer className="border-t-4 border-slate-900 bg-slate-900 text-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 grid md:grid-cols-3 gap-6 font-mono-display text-xs uppercase tracking-widest">
          <div>
            <div className="text-pink-400 mb-2 flex items-center gap-2">
              <Sparkles size={14} strokeWidth={2.5} /> Kimmypedia
            </div>
            <div className="text-slate-400 normal-case tracking-normal">
              A community-edited lexicon for the Netflix series <em>Unbreakable Kimmy Schmidt</em> (2015–2020). Not affiliated with Netflix or anyone named Reverend.
            </div>
          </div>
          <div>
            <div className="text-yellow-400 mb-2">Hatch Controls</div>
            <ul className="space-y-1 normal-case tracking-normal text-slate-400">
              <li>• No login — just type &amp; contribute</li>
              <li>• Sections sorted by timestamp</li>
              <li>• Season 1–4 + Kimmy vs the Reverend</li>
            </ul>
          </div>
          <div>
            <div className="text-cyan-400 mb-2">Catchphrase</div>
            <div className="text-slate-200 text-lg normal-case tracking-normal">
              "Females are strong as hell." 💪
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-4 py-3 font-mono-display text-xs uppercase tracking-widest border-r-2 border-slate-700 transition-colors ${
          isActive ? "bg-yellow-400 text-slate-900" : "hover:bg-pink-500 hover:text-white"
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function Sidebar() {
  return (
    <div className="brutal-card p-4 sticky top-6" data-testid="sidebar">
      <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-2">
        📁 Browse by Season
      </div>
      <ul className="space-y-1">
        {Object.entries(SEASON_LABELS).map(([num, label]) => (
          <li key={num}>
            <Link
              to={`/episodes?season=${num}`}
              className="wiki-link block py-1 text-sm"
              data-testid={`sidebar-season-${num}`}
            >
              › {label}
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-4 border-t-2 border-slate-900">
        <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-2">
          📄 Quick Links
        </div>
        <ul className="space-y-1 text-sm">
          <li>
            <Link to="/episodes" className="wiki-link" data-testid="sidebar-all-episodes">
              › All Episodes
            </Link>
          </li>
          <li>
            <Link to="/search" className="wiki-link" data-testid="sidebar-search">
              › Search the Wiki
            </Link>
          </li>
        </ul>
      </div>
      <div className="mt-4 pt-4 border-t-2 border-slate-900 bg-yellow-200 -mx-4 -mb-4 p-4">
        <div className="font-mono-display text-xs uppercase tracking-widest text-slate-900 mb-1">
          ⚠ Broadcast
        </div>
        <div className="text-xs text-slate-900">
          Anyone can add a section. Be kind. Reference timestamps like <code className="font-mono-display">12:34</code>.
        </div>
      </div>
    </div>
  );
}
